import { v4 as uuidv4 } from 'uuid';

/**
 * Genera un teamCode único a partir del nombre del equipo.
 * El teamCode consiste en 5 letras (se eliminan espacios y se pasa a mayúsculas).
 * Si el nombre es menor a 5 caracteres, se rellena con letras aleatorias.
 * Se puede pasar un arreglo de códigos ya existentes para asegurar la unicidad.
 *
 * @param {any} name - El nombre del equipo.
 * @param {string[]} [existingCodes=[]] - Arreglo opcional de códigos ya existentes.
 * @returns {string} El teamCode generado o una cadena vacía en caso de error.
 */
export function generateTeamCodeFromName(name, existingCodes = []) {
  if (typeof name !== "string") {
    console.error("generateTeamCodeFromName esperaba un string pero recibió:", name);
    return '';
  }
  // Elimina espacios y convierte a mayúsculas.
  const cleaned = name.replace(/\s+/g, '').toUpperCase();
  let baseCode = cleaned.substring(0, 5);
  // Si el código base tiene menos de 5 caracteres, se rellena con letras aleatorias.
  while (baseCode.length < 5) {
    baseCode += String.fromCharCode(65 + Math.floor(Math.random() * 26));
  }
  let teamCode = baseCode;
  let attempts = 0;
  // Aseguramos la unicidad del código.
  while (existingCodes.includes(teamCode) && attempts < 100) {
    teamCode = '';
    for (let i = 0; i < 5; i++) {
      teamCode += String.fromCharCode(65 + Math.floor(Math.random() * 26));
    }
    attempts++;
  }
  return teamCode;
}

/**
 * Crea o actualiza un equipo con el nombre dado y un logo opcional.
 * Si ya existe un equipo con el mismo nombre (sin distinguir mayúsculas/minúsculas)
 * en la competencia, se devuelve el equipo existente; en caso contrario, se crea uno nuevo.
 * Se genera un teamCode único utilizando la función generateTeamCodeFromName.
 *
 * @param {Object} competition - Objeto de competencia que contiene el arreglo de equipos.
 * @param {string} teamName - El nombre del equipo.
 * @param {string} [logo=''] - Logo opcional del equipo.
 * @returns {Object} Un objeto con:
 *    - team: El equipo nuevo o actualizado.
 *    - equiposActualizados: El arreglo actualizado de equipos para la competencia.
 */
export function createOrUpdateTeam(competition, teamName, logo = '') {
  let equiposActualizados = Array.isArray(competition.equipos)
    ? [...competition.equipos]
    : [];

  if (typeof teamName !== 'string') {
    console.error("createOrUpdateTeam esperaba un string en teamName pero recibió:", teamName);
    return { team: null, equiposActualizados };
  }

  const existingTeam = equiposActualizados.find(
    (eq) =>
      typeof eq === 'object' && eq.name.toLowerCase() === teamName.toLowerCase()
  );

  if (!existingTeam) {
    const existingCodes = equiposActualizados.map(eq => eq.teamCode);
    const newTeam = {
      id: uuidv4(),
      name: teamName,
      teamCode: generateTeamCodeFromName(teamName, existingCodes),
      logoEquipo: logo,
    };
    equiposActualizados.push(newTeam);
    return { team: newTeam, equiposActualizados };
  } else {
    if (logo && !existingTeam.logoEquipo) {
      existingTeam.logoEquipo = logo;
    }
    return { team: existingTeam, equiposActualizados };
  }
}
